import { companyDashboardLinks } from "@/dashboards/companyDashboardsDomain";
import {
  accountMenuLinks,
  AppBarData,
  companyStatusHeading,
  CompanyOption,
  prepHelpCenterLink,
  prepLanguages,
  prepMenuLinks,
  prepPortfolioLinks,
  prepUser,
} from "./appBarDomain";
import {
  adminDashboard,
  adminSettings,
  adminShareholderStatement,
  adminShareholdersTransaction,
  companyUrl,
  giveUsFeedback,
  userSettingsUrl,
} from "@/utils/navigation";
import { isMobileView } from "@/utils/screen";
import { openCookieSettings } from "@/cookies/cookies";
import { changeLanguageAction } from "@/i18n/i18nDomain";
import {
  extractPortfolioCompaniesUrl,
  extractPortfolioUrl,
  portfolioAllCompanies,
  PortfolioHeader,
  portfolioInstance,
  SelectedPortfolioOption,
  sumFollowUpCounts,
} from "@/portfolio/portfolioDomain";
import {
  useDispatch,
  useLanguage,
  useRootStore,
  useShareholdersStore,
  useUserPrefs,
  useUserStore,
} from "@/stores/RootStoreContext";
import { now } from "lodash";
import { CompanyItem, isOk, SigningMethod } from "@/common/common";
import { navigate } from "@/events/eventsDomain";
import { TMessage } from "@/i18n/tt";
import { formatUserName, GetUserSettingsResponse } from "@/types/UserInfo";
import { isOptionHolderOnly } from "@/types/Shareholder";
import { when } from "@/utils/functions";
import { isBefore, toDate } from "@/utils/Dates";
import { AlertMessage } from "@/stores/AlertMessageStore";
import { AccessType } from "@/types/enums/AccessType";
import { isNorwegianOrgNo, isSwedishOrgNo } from "@/utils/orgNo";

const goToPortfolioTooltip = (portfolio: SelectedPortfolioOption | undefined): TMessage => {
  if (portfolio) {
    return { key: "app-bar.back-to", params: { context: portfolio.label } };
  }
  return { key: "go-to", params: { context: portfolioAllCompanies.label } };
};

const backToPortfolio = (
  portfolio: SelectedPortfolioOption,
  isPortfolioPage: boolean,
): { url: string; label: TMessage } | undefined => {
  if (isPortfolioPage && portfolio.type === "all-companies") {
    return undefined;
  }
  const target = isPortfolioPage ? portfolioAllCompanies : portfolio;
  return { url: extractPortfolioCompaniesUrl(target), label: goToPortfolioTooltip(target) };
};

const getLastVisited = (c: CompanyOption, userSettings: GetUserSettingsResponse | null): number =>
  when(userSettings?.lastVisitedCompanies?.[c.organisationNumber], (ts) => toDate(ts).getTime()) ??
  0;

const getRecentCompanies = (
  selectedCompany: CompanyOption | undefined,
  {
    companies,
    userSettings,
    limit = 10,
  }: { companies: CompanyOption[]; userSettings: GetUserSettingsResponse | null; limit?: number },
): CompanyOption[] => {
  return companies
    .filter(
      (c) =>
        c.organisationNumber !== selectedCompany?.organisationNumber &&
        getLastVisited(c, userSettings),
    )
    .toSorted((a, b) => getLastVisited(b, userSettings) - getLastVisited(a, userSettings))
    .slice(0, limit);
};

export function useAppBar({
  withoutMenu,
  withoutUserSettings,
  isPortfolioPage,
  openPortfolioSettings,
  portfolioHeader,
  hideSubscriptionButton,
  selectedPortfolioOption,
  notification,
}: {
  withoutMenu?: boolean;
  withoutUserSettings?: boolean;
  isPortfolioPage?: boolean;
  openPortfolioSettings?: () => void;
  portfolioHeader?: PortfolioHeader;
  hideSubscriptionButton?: boolean;
  selectedPortfolioOption?: SelectedPortfolioOption;
  notification?: AlertMessage;
} = {}): AppBarData | null {
  const locale = useLanguage();
  const { pathname: currentUrl } = useRootStore().location;
  const { history, locationStr } = useRootStore();
  const userStore = useUserStore();
  const shareholdersStore = useShareholdersStore();
  const isMobile = isMobileView();
  const dispatch = useDispatch();
  const userPrefs = useUserPrefs();
  const { portfolioState, followUpCounts, companyDashboards } = useRootStore();
  if (!userStore.user) {
    return null;
  }
  const userRole = userStore.userRole;
  const portfolio = selectedPortfolioOption ?? portfolioState?.portfolio ?? portfolioAllCompanies;
  const portfolioUrl = extractPortfolioCompaniesUrl(portfolio);
  const backTo = backToPortfolio(portfolio, isPortfolioPage ?? false);
  const hasEnterpriseAccount = userStore.enterpriseAccountItems.length > 0;
  const shareholderView = isOk(userStore.shareholderViewResult)
    ? userStore.shareholderViewResult.data
    : undefined;
  const menuLinks = withoutMenu
    ? undefined
    : isPortfolioPage
      ? prepPortfolioLinks({
          currentUrl,
          portfolio,
          portfolioFollowUpCount: sumFollowUpCounts(followUpCounts),
          showContacts: userStore.hasAnyAdminRole || hasEnterpriseAccount,
          portfolioFeatureToggles: userStore.availablePortfolioFeatures,
        })
      : prepMenuLinks({
          orgNumber: userStore.orgNumber,
          accessType: userStore.accessType,
          currentUrl,
          isMobile,
          portfolioUrl,
          shareholderId: userStore.shareholderId,
          country: userStore?.companyInfo?.company.country,
          subscription: userStore.paidSubscription,
          userPrefs,
          userRole,
          followUpCount: userStore.companyInfo?.followUpCount,
          featureToggles: userStore.featureToggles,
          ownershipModel: userStore.companyInfo?.ownershipModel,
          showShareClasses: !userStore.companyInfo?.hasAutomaticShareStructure,
          companyDashboards: companyDashboardLinks(companyDashboards, userStore.orgNumber, locale),
          shareholderHasOptions: shareholderView?.options !== undefined,
          shareholderIsOptionHolderOnly:
            shareholderView !== undefined && isOptionHolderOnly(shareholderView),
        });
  const selectedCompany = isPortfolioPage
    ? undefined
    : userStore.companies.find(
        (c) =>
          c.organisationNumber === userStore.orgNumber &&
          c.accessType === userStore.accessType &&
          c.shareholderId === userStore.shareholderId,
      );
  const statusHeading = companyStatusHeading(
    userStore.companyInfo?.company?.status,
    userStore.companyInfo?.companyStatusChange,
  );

  return {
    portfolioHeader,
    portfolioUrl: backTo?.url ?? portfolioUrl,
    notification,
    backToPortfolioLabel: backTo?.label,
    selectedCompany,
    selectedPortfolioOption: portfolio,
    languages: prepLanguages(locale),
    user: prepUser(userStore.user),
    links: menuLinks,
    subLinks: menuLinks?.find((l) => l.isActive)?.subLinks,
    helpCenter: isPortfolioPage
      ? undefined
      : prepHelpCenterLink({ currentUrl, icon: "question", portfolio: selectedPortfolioOption }),
    companies: getRecentCompanies(selectedCompany, {
      companies: userStore.companies,
      userSettings: userStore.userSettings,
    }),
    portfolios: userStore.portfolios,
    subscription: userStore.paidSubscription,
    signDocumentCounts: isPortfolioPage ? undefined : userStore.signDocumentCounts,
    actions: {
      addAdministrator: (companyItem: CompanyItem) =>
        userStore.showInviteUserDialog(companyItem, locale),
      changeLanguage: (lang) => {
        dispatch(changeLanguageAction(lang));
        userStore.updateUserLanguage(lang);
      },
      logOut: userStore.logout,
      showContactSaleAndSupport: true,
      showPricesLink: true,
      navigateToUserSettings: withoutUserSettings
        ? undefined
        : () => dispatch([navigate(userSettingsUrl(locationStr))]),
      openCompanySettings: () => history.push(adminSettings(userStore.orgNumber)),
      openPaymentDialog:
        hideSubscriptionButton || userStore.userRole.allowCompanySettingsEdit !== true
          ? undefined
          : [navigate(adminSettings(userStore.orgNumber) + "/billing")],
      openHelpCenter: () => {
        if (userStore.orgNumber) {
          history.push(adminDashboard(userStore.orgNumber) + "/help-center");
        } else {
          history.push(portfolioUrl + "/help-center");
        }
      },
      openCookieSettings,
      selectCompany: (c) => {
        userStore.clearCompany();
        userStore.setSelectedCompany({
          organisationNumber: c.organisationNumber,
          accessType: c.accessType,
          shareholderId: c.shareholderId,
        });
        shareholdersStore.clearTrankMap();
        history.push(
          resolveContextCompanyUrl({
            currentUrl,
            accessType: c.accessType,
            orgNo: c.organisationNumber,
            shareholderId: c.shareholderId,
          }),
        );
      },
      upgradeToPro:
        hideSubscriptionButton || userStore.userRole.allowCompanySettingsEdit !== true
          ? undefined
          : userStore.openPaymentDialog,
      navigateToFeedback: giveUsFeedback,
      openCreatePortfolioDialog: userStore.hasAnyAdminRole
        ? () => userStore.openCreatePortfolioDialog()
        : undefined,
      openBatchSigningPage: (signingMethod: SigningMethod) =>
        userStore.navigateToBatchSigningPage(signingMethod, currentUrl),
      filterCompaniesInPortfolio: (type) => {
        dispatch([
          { type: "update-store", data: { followUpCounts: undefined } },
          { type: "navigate", url: extractPortfolioUrl(type, portfolioHeader) },
        ]);
      },
      openPortfolio: (portfolio) => {
        dispatch([
          {
            type: "navigate",
            url: extractPortfolioUrl(portfolioInstance(portfolio), portfolioHeader),
          },
        ]);
      },
      openPortfolioSettings,
    },
    liquidationLabel: userStore.companyInfo?.liquidatedTransactionDate
      ? {
          key: isBefore(userStore.companyInfo?.liquidatedTransactionDate, now())
            ? "app-bar.liquidated"
            : "app-bar.will-liquidate",
          params: {
            date: when(userStore.companyInfo?.liquidatedTransactionDate, (date) => ({
              type: "date",
              value: date,
            })),
          },
        }
      : undefined,
    liquidationInfoText:
      userStore.companyInfo?.liquidatedTransactionDate &&
      userStore.companyInfo?.liquidatedTransactionId
        ? {
            key: "app-bar.liquidated-info-text",
            params: {
              user: formatUserName(userStore.user),
              companyName: userStore.companyName,
              orgNo: userStore.orgNumber,
              date: when(userStore.companyInfo.liquidatedTransactionDate, (date) => ({
                type: "date",
                value: date,
              })),
              transactionUrl: adminShareholdersTransaction(
                userStore.orgNumber,
                userStore.companyInfo.liquidatedTransactionId,
              ),
              shareholderLink: adminShareholderStatement(userStore.orgNumber),
            },
          }
        : undefined,
    mergedLabel: userStore.companyInfo?.mergedTransactionDate
      ? {
          key: isBefore(userStore.companyInfo?.mergedTransactionDate, now())
            ? "app-bar.merged"
            : "app-bar.will-merge",
          params: {
            date: when(userStore.companyInfo?.mergedTransactionDate, (date) => ({
              type: "date",
              value: date,
            })),
          },
        }
      : undefined,
    mergedInfoText:
      userStore.companyInfo?.mergedTransactionDate && userStore.companyInfo?.mergedTransactionId
        ? {
            key: "app-bar.merged-info-text",
            params: {
              user: formatUserName(userStore.user),
              companyName: userStore.companyName,
              orgNo: userStore.orgNumber,
              acquiror: userStore.companyInfo.mergedAcquirorName,
              date: when(userStore.companyInfo.mergedTransactionDate, (date) => ({
                type: "date",
                value: date,
              })),
              transactionUrl: adminShareholdersTransaction(
                userStore.orgNumber,
                userStore.companyInfo.mergedTransactionId,
              ),
              shareholderLink: adminShareholderStatement(userStore.orgNumber),
            },
          }
        : undefined,
    companyStatusLabel: statusHeading.label,
    companyStatusInfoText: statusHeading.infoText,
    accountMenuLinks: isPortfolioPage
      ? accountMenuLinks(
          userStore.enterpriseAccountItems,
          userStore.hasSuperadminAccess,
          currentUrl,
        )
      : undefined,
  };
}

function isUnresolvableUrlForCompany(restParts: string[], orgNo: string): boolean {
  const [mainPath, subPath] = restParts;
  if (mainPath === "shareholders") {
    if (subPath === "beneficial-owners" || subPath === "statement") return !isNorwegianOrgNo(orgNo);
    if (subPath === "commitments") return true;
    if (subPath === "contributions") return !isSwedishOrgNo(orgNo);
  }
  if (mainPath === "settings" && subPath === "altinn") return !isNorwegianOrgNo(orgNo);
  return restParts.length < 1;
}

export function resolveContextCompanyUrl({
  currentUrl,
  accessType,
  orgNo,
  shareholderId,
}: {
  currentUrl: string;
  accessType: AccessType;
  orgNo: string;
  shareholderId?: number;
}): string {
  if (accessType !== AccessType.ADMIN) {
    return companyUrl(orgNo, accessType, shareholderId);
  }
  const parts = currentUrl.split("?")[0].split("/").filter(Boolean);
  const companyIndex = parts.indexOf("company");
  if (companyIndex === -1 || companyIndex + 1 >= parts.length) {
    return companyUrl(orgNo, accessType, shareholderId);
  }
  const restParts = parts.slice(companyIndex + 2);
  if (isUnresolvableUrlForCompany(restParts, orgNo)) {
    return companyUrl(orgNo, accessType, shareholderId);
  }
  if (restParts[0] === "archive" && restParts[1] === "custom-archive") {
    return `/company/${orgNo}/archive/admin-archive`;
  }

  const firstIdIndex = restParts.findIndex((item) => /\d/.test(item));
  const baseParts = firstIdIndex === -1 ? restParts : restParts.slice(0, firstIdIndex);
  return `/company/${orgNo}/${baseParts.join("/")}`;
}
